import React, { useEffect, useRef, useState } from 'react'
import Portal from '../../helpers/Portal'
import { useBetterKeyPress } from '../../helpers/useKeyPress'

const Dialog = React.forwardRef(
  ({ onClose, children, as: Component = 'div', ...props }, ref) => {
    const internalRef = useRef(null)
    const containerRef = ref || internalRef
    const [activeElement, setActiveElement] = useState(null)
    const focusRestored = useRef(false)

    useEffect(() => {
      setActiveElement(document.activeElement)
    }, [])

    const restoreFocus = () => {
      if (focusRestored.current) return
      document.body.style.overflow = ''
      const root = document.getElementById('___gatsby')

      if (root) {
        root.removeAttribute('inert')
        root.removeAttribute('aria-hidden')
      }
      if (activeElement) {
        // setTimeout(() => activeElement.focus())
        activeElement.focus()
      }
      focusRestored.current = true
    }

    useEffect(() => {
      document.body.style.overflow = 'hidden'

      const root = document.getElementById('___gatsby')
      if (root) {
        root.setAttribute('inert', '')
        root.setAttribute('aria-hidden', 'true')
      }

      // Check if focus is already within the container (e.g. set by child)
      if (
        containerRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        containerRef.current.focus()
      }

      // return () => {
      //   restoreFocus()
      // }
    }, [activeElement, containerRef])

    useEffect(() => {
      const handleTabKey = (e) => {
        if (e.key !== 'Tab') return
        if (!containerRef.current) return

        const focusableSelectors = [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(',')

        const focusableElements = Array.from(
          containerRef.current.querySelectorAll(focusableSelectors)
        ).filter((el) => el.offsetParent !== null)

        if (focusableElements.length === 0) return

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault()
            lastElement.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault()
            firstElement.focus()
          }
        }
      }

      document.addEventListener('keydown', handleTabKey)
      return () => {
        document.removeEventListener('keydown', handleTabKey)
      }
    }, [])

    const handleClose = () => {
      restoreFocus()
      onClose()
    }

    const esc = useBetterKeyPress('Escape')
    useEffect(() => {
      esc && handleClose()
    }, [esc, onClose])

    return (
      <Portal>
        <Component ref={containerRef} tabIndex="-1" {...props}>
          {typeof children === 'function'
            ? children({ close: handleClose })
            : children}
        </Component>
      </Portal>
    )
  }
)

export default Dialog
